import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  isConformingExecutionName,
  listPipelineRuns,
  PipelineRun,
  PipelineRunStatus,
} from '../../api/taxPipeline';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { useEscToClose } from '../../hooks/useEscToClose';
import DataTable from '../shared/DataTable';
import RunPipelineModal from './RunPipelineModal';
import PipelineRunDetailPane, { isTerminal, STATUS_BADGE_CLASS } from './PipelineRunDetailPane';
import { formatRuntime, formatTimestamp } from './taxReportingUtils';
import './PipelineRunsView.css';

// =============================================================================
// PipelineRunsView (CTR-014 TASK-016 + redesign_pipeline_runs_ux TASK-008)
//
// Lists tax-pipeline executions via Step Functions. The list endpoint is
// cursor-based (`nextToken`), so we maintain a stack of seen cursors to
// support Prev/Next pagination over the cursor stream while still satisfying
// DataTable's pageIndex/pageSize contract.
//
// Filters: tax-year (default = section's selected year, with an "all years"
// toggle that omits the filter from the API call) and status (single select —
// the SfnClient list filter accepts a single status, mirrored in the UI).
//
// Detail viewing is in-place: clicking a conforming row toggles a
// PipelineRunDetailPane in DataTable's expandedRowContent slot. The "Run
// Pipeline" button opens RunPipelineModal; on success we invalidate the runs
// query and flash the just-created row.
//
// Per CLAUDE.md, server-paginated tables MUST be server-sorted. The list
// endpoint returns Step Functions executions in `startedAt DESC` order; the
// status column is also sortable client-locally for the visible page.
// =============================================================================

// Polling cadence for the runs list while any visible run is non-terminal (CTR-018 AC-4).
const RUNS_POLL_INTERVAL_MS = 10000;

/**
 * TanStack Query `refetchInterval` predicate for the runs list (CTR-018 AC-4, REQ-006).
 * Returns the poll cadence (ms) while at least one visible run is non-terminal, and
 * `false` (halt — no further requests) when every visible run is terminal, the list is
 * empty, or the last fetch errored. Exported for direct unit testing.
 */
export function computeRunsRefetchInterval(
  runs: PipelineRun[] | undefined,
  hasError: boolean
): number | false {
  if (hasError) {
    return false;
  }
  const anyNonTerminal = (runs ?? []).some((run) => !isTerminal(run.status));
  return anyNonTerminal ? RUNS_POLL_INTERVAL_MS : false;
}

const ALL_STATUSES: PipelineRunStatus[] = [
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'TIMED_OUT',
  'ABORTED',
];

function joinOrUnknown(value: readonly string[] | 'unknown'): string {
  return value === 'unknown' ? 'unknown' : value.join(', ');
}

export const PipelineRunsView: React.FC = () => {
  const queryClient = useQueryClient();
  const sectionTaxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [allYears, setAllYears] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PipelineRunStatus | ''>('');
  const [pageSize, setPageSize] = useState(25);
  // Snapshot of "now" used to render elapsed-time for RUNNING rows. Bumped on
  // every global auto-refresh tick so the runtime column advances at the same
  // cadence as the data refetch — single source of truth. When auto-refresh is
  // off, refreshTrigger stops bumping and the timer freezes alongside the rest
  // of the dashboard snapshot.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    setNowMs(Date.now());
  }, [refreshTrigger]);

  const [modalOpen, setModalOpen] = useState(false);
  const [justCreatedExecutionName, setJustCreatedExecutionName] = useState<
    string | null
  >(null);
  const [expandedExecutionName, setExpandedExecutionName] = useState<string | null>(
    null
  );

  // Cursor stack: cursorStack[i] is the nextToken used to fetch page i.
  // pageIndex 0 → no cursor; pageIndex N → cursorStack[N-1].
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'startedAt', desc: true },
  ]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0]
    ? sorting[0].desc
      ? 'desc'
      : 'asc'
    : undefined;

  const taxYearForApi = allYears ? undefined : sectionTaxYear;
  const cursor = pageIndex === 0 ? undefined : cursorStack[pageIndex - 1];

  const resetPagination = () => {
    setPageIndex(0);
    setCursorStack([]);
  };

  useEscToClose(() => setExpandedExecutionName(null), {
    disabled: expandedExecutionName === null,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'tax',
      'pipeline-runs',
      taxYearForApi,
      statusFilter,
      sortBy,
      sortDir,
      pageIndex,
      // cursor (nextToken) is a fetch input — include it in the key so a polling
      // refetch (CTR-018 AC-4) on page N never shares a cache slot with another
      // page's result (crypto-trading-ui/CLAUDE.md queryKey discipline).
      cursor,
      pageSize,
      refreshTrigger,
    ],
    queryFn: () =>
      listPipelineRuns({
        taxYear: taxYearForApi,
        status: statusFilter || undefined,
        nextToken: cursor,
      }),
    // CTR-018 AC-4: poll the runs list while any visible run is non-terminal so a
    // RUNNING job transitions to a terminal state without a manual refresh; halt
    // (no further requests) once every visible run is terminal, on error, or when
    // there are no runs. refetchIntervalInBackground:false pauses polling while the
    // browser tab is hidden (REQ-007) — no custom visibilitychange listener needed,
    // and the global refetchOnWindowFocus:false stays intact.
    refetchInterval: (query) =>
      computeRunsRefetchInterval(query.state.data?.items, !!query.state.error),
    refetchIntervalInBackground: false,
  });

  const items = data?.items ?? [];
  const nextToken = data?.nextToken;

  // Cursor-paginated APIs do not return total counts; advertise pageCount as
  // pageIndex+2 when nextToken is present (so Next is enabled), else
  // pageIndex+1 (so Next is disabled).
  const pageCount = nextToken ? pageIndex + 2 : pageIndex + 1;

  const onPageIndexChange = (next: number) => {
    if (next === pageIndex) return;
    if (next > pageIndex && nextToken) {
      // Forward navigation: append the current response's nextToken.
      setCursorStack((prev) => {
        const trimmed = prev.slice(0, pageIndex);
        return [...trimmed, nextToken];
      });
      setPageIndex(next);
    } else if (next < pageIndex) {
      setPageIndex(Math.max(0, next));
    } else if (next === 0) {
      resetPagination();
    }
  };

  const columns = useMemo<ColumnDef<PipelineRun>[]>(
    () => [
      {
        id: 'startedAt',
        accessorKey: 'startedAt',
        header: 'Started',
        cell: (info) => (
          <span className="date-cell">
            {formatTimestamp(info.getValue() as string)}
          </span>
        ),
      },
      {
        id: 'runtime',
        header: 'Runtime',
        cell: (info) => {
          const run = info.row.original;
          return (
            <span className="date-cell">
              {formatRuntime(run.startedAt, run.finishedAt, nowMs)}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const s = info.getValue() as PipelineRunStatus;
          return <span className={STATUS_BADGE_CLASS[s] ?? 'badge'}>{s}</span>;
        },
      },
      {
        id: 'taxYear',
        accessorKey: 'taxYear',
        header: 'Year',
      },
      {
        id: 'methods',
        header: 'Methods',
        cell: (info) => joinOrUnknown(info.row.original.methods),
        enableSorting: false,
      },
      {
        id: 'formats',
        header: 'Formats',
        cell: (info) => joinOrUnknown(info.row.original.formats),
        enableSorting: false,
      },
      {
        id: 'triggeredBy',
        header: 'Triggered By',
        cell: (info) => {
          const t = info.row.original.triggeredBy;
          return t === 'unknown' ? 'unknown' : t.email;
        },
        enableSorting: false,
      },
      {
        id: 'executionName',
        accessorKey: 'executionName',
        header: 'Execution',
        cell: (info) => (
          <span className="order-id-cell">{info.getValue() as string}</span>
        ),
        enableSorting: false,
      },
    ],
    [nowMs]
  );

  return (
    <div aria-label="Pipeline Runs view">
      <div className="table-filters">
        <div className="filter-group">
          <label htmlFor="pr-all-years">
            <input
              id="pr-all-years"
              type="checkbox"
              checked={allYears}
              onChange={(e) => {
                setAllYears(e.target.checked);
                resetPagination();
              }}
            />{' '}
            All years
          </label>
        </div>
        {!allYears && (
          <div className="filter-group">
            <span>Tax Year: {sectionTaxYear}</span>
          </div>
        )}
        <div className="filter-group">
          <label htmlFor="pr-status-filter">Status:</label>
          <select
            id="pr-status-filter"
            value={statusFilter || 'ALL'}
            onChange={(e) => {
              const v = e.target.value;
              setStatusFilter(v === 'ALL' ? '' : (v as PipelineRunStatus));
              resetPagination();
            }}
          >
            <option value="ALL">All</option>
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="table-filters__actions">
          <button
            type="button"
            className="modal-pill-button modal-pill-primary"
            onClick={() => setModalOpen(true)}
          >
            Run Pipeline
          </button>
        </div>
      </div>

      <DataTable<PipelineRun>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            resetPagination();
          },
        }}
        rowClassName={(run) => {
          const classes: string[] = [];
          if (run.executionName === justCreatedExecutionName) {
            classes.push('pipeline-run-row--just-created');
          }
          if (!isConformingExecutionName(run.executionName)) {
            classes.push('pipeline-run-row--non-conforming');
          }
          return classes.length > 0 ? classes.join(' ') : undefined;
        }}
        onRowClick={(run) => {
          setExpandedExecutionName((prev) =>
            prev === run.executionName ? null : run.executionName
          );
        }}
        expandedRowContent={(run) => {
          if (expandedExecutionName !== run.executionName) return null;
          if (!isConformingExecutionName(run.executionName)) {
            return (
              <div className="pipeline-run-detail-pane pipeline-run-detail-pane--legacy">
                <h3>Legacy execution — details unavailable</h3>
                <p>
                  This run's execution name (<code>{run.executionName}</code>)
                  does not match the current naming convention
                  (<code>tax-{'{year}'}-{'{minute-bucket}'}</code>). It was likely
                  auto-named by AWS Step Functions before the deterministic
                  naming convention shipped, and the backend does not accept
                  this name format on the detail endpoint.
                </p>
                <p>
                  No details or report files can be fetched for this run.
                  Submit a new run via <strong>Run Pipeline</strong> to see the
                  inline detail pane in action.
                </p>
              </div>
            );
          }
          return <PipelineRunDetailPane executionName={run.executionName} />;
        }}
        loadingMessage="Loading pipeline runs…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={
          error instanceof Error ? error.message : 'Failed to load pipeline runs'
        }
        emptyMessage="No pipeline runs match these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: undefined,
          onPageIndexChange,
          onPageSizeChange: (size) => {
            setPageSize(size);
            resetPagination();
          },
        }}
      />

      {modalOpen && (
        <RunPipelineModal
          onClose={() => setModalOpen(false)}
          onSuccess={(run) => {
            queryClient.invalidateQueries({ queryKey: ['tax', 'pipeline-runs'] });
            setJustCreatedExecutionName(run.executionName);
            setTimeout(() => setJustCreatedExecutionName(null), 3000);
            setModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default PipelineRunsView;
